import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../db/api.js'

// Polling do status da coleta (fila sync_jobs).
//
// Comportamento:
//   - Busca /api/sync-jobs ao montar.
//   - Enquanto houver job ativo (pending/running), repete a cada `intervalMs`.
//   - Quando a coleta termina (active passa de true → false), dispara
//     `onComplete` uma única vez — o dashboard usa isso para refazer o fetch
//     dos dados e o banner para trocar pro estado "concluído".
//   - Para de fazer polling quando não há nada ativo (não martela o backend).
//
// Retorna { jobs, active, justCompleted, loading, refresh }.
export function useSyncStatus({ intervalMs = 5000, onComplete } = {}) {
  const [jobs, setJobs] = useState([])
  const [active, setActive] = useState(false)
  const [justCompleted, setJustCompleted] = useState(false)
  const [loading, setLoading] = useState(true)

  const wasActiveRef = useRef(false)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const fetchOnce = useCallback(async () => {
    try {
      const data = await api.getSyncJobs()
      const nextActive = !!data?.active
      setJobs(data?.jobs || [])
      setActive(nextActive)

      // Transição ativo → inativo: a coleta acabou nesta volta.
      if (wasActiveRef.current && !nextActive) {
        setJustCompleted(true)
        onCompleteRef.current?.()
      }
      wasActiveRef.current = nextActive
      return nextActive
    } catch (err) {
      console.warn('[useSyncStatus] getSyncJobs falhou:', err?.message)
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let timer = null
    let cancelled = false

    const tick = async () => {
      if (cancelled) return
      const stillActive = await fetchOnce()
      if (cancelled) return
      // Só reagenda enquanto houver coleta em andamento.
      if (stillActive) timer = setTimeout(tick, intervalMs)
    }

    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [fetchOnce, intervalMs])

  // Permite descartar o estado "concluído" (ex.: usuário fecha o banner).
  const dismissCompleted = useCallback(() => setJustCompleted(false), [])

  return { jobs, active, justCompleted, loading, refresh: fetchOnce, dismissCompleted }
}
